/**
 * Client Reviews API module
 */
import type { ClientReviewVersionHistory as ReviewVersionHistoryData } from '@overlayqa/shared';
import type { ClientReview, CreateClientReviewRequest, ClientEmailInvite, ClientReviewRecipient } from '@overlayqa/shared';
import { fetchApi, fetchApiEnvelope, resolveAssetUrl } from './client';
import { log } from '@/lib/logger';
import { MUTATION_TIMEOUT_MS, SCREEN_READ_TIMEOUT_MS } from './requestTimeout';

log.info('[api/clientReviews] Module loaded');

/**
 * Upper bound on the review handoff re-mint, the request from the incident that
 * produced GitHub issue #75.
 *
 * Deliberately UNDER ReviewHandoffPage's own HANDOFF_DEADLINE_MS (15s). That
 * one is a wall clock over getToken() + this POST and it reaps the attempt
 * anonymously: the request stays pending, so nothing is emitted and the stall
 * is invisible, which is exactly how user-0fa99b@example.com's 19.2s dead end
 * on 2026-08-12 left zero api_network_failure and zero $exception behind.
 * Bounding the request lower means a stalled POST fails as a POST, carrying its
 * endpoint into analytics, while the page deadline stays as the backstop for
 * the getToken() leg it alone can cover.
 *
 * 12s: the route does three indexed queries and an HMAC sign, so a healthy
 * answer is far inside this even from a cold start.
 */
export const HANDOFF_REQUEST_TIMEOUT_MS = 12_000;

/**
 * Upper bound on the free-scan claim (OVE-4). Same shape of work as the handoff
 * re-mint above (a few indexed writes, no external calls), so 12s is far inside
 * a healthy answer even from a Railway cold start, while a stalled POST still
 * fails AS a POST (carrying its endpoint into api_network_failure) instead of
 * hanging invisibly the way the 2026-08-12 unbounded handoff did.
 */
export const CLAIM_REQUEST_TIMEOUT_MS = 12_000;

/** Result of a successful claim — the re-parented project and how much moved. */
export interface ClaimFreeScanResult {
  projectId: string;
  teamId: string;
  ticketCount: number;
  alreadyClaimed: boolean;
}

/**
 * Live-review creation result.
 *
 * Normal path: server returns shareUrl and opens a proxy/snapshot review.
 * SPA auto-switch: modeSwitched=true + switchReason — surface a notice instead
 *   of silently opening a non-navigable snapshot.
 * Streamed path: mode='streamed' + liveViewUrl — the server provisioned a Steel
 *   remote-browser session; liveViewUrl points at Steel's viewer endpoint.
 */
export type CreateLiveReviewResult = ClientReview & {
  shareUrl: string;
  liveAvailable: boolean;
  modeSwitched: boolean;
  switchReason?: string;
  /** Present when responseMode === 'streamed'. Steel viewer URL for the live session. */
  liveViewUrl?: string;
  /**
   * Envelope-level mode returned by the server. Distinct from ClientReview.mode
   * (the entity field, which is 'live' | 'snapshot'). This discriminates between
   * the proxy/snapshot path and the Steel-backed streamed path.
   */
  responseMode?: 'live' | 'snapshot' | 'streamed';
  /**
   * Short-TTL auth handoff token. The review page renders at the API origin
   * where the dashboard's Clerk cookie may not reach (prod app.->api. split),
   * so we append this to the review URL (?s=) to authenticate the render
   * deterministically. Absent on older servers.
   */
  authHandoff?: string;
};

export const clientReviewsApi = {
  versions: async (token:string|undefined,id:string):Promise<ReviewVersionHistoryData> => {
    const history = await fetchApi<ReviewVersionHistoryData>('/client-reviews/'+id+'/versions',{token,timeoutMs:SCREEN_READ_TIMEOUT_MS});
    return { ...history, versions: history.versions.map(version => ({ ...version, screenshotUrl: resolveAssetUrl(version.screenshotUrl) || null })) };
  },
  createVersion: (token:string|undefined,id:string,body:{name:string;requestId:string;baseVersionId:string|null}):Promise<unknown> => fetchApi('/client-reviews/'+id+'/versions',{token,method:'POST',timeoutMs:MUTATION_TIMEOUT_MS,body:JSON.stringify(body)}),
  create: async (token: string | undefined, data: CreateClientReviewRequest): Promise<ClientReview & { shareUrl: string; invites: ClientEmailInvite[] }> => {
    log.info('[api/clientReviews] create', { projectId: data.projectId, clientEmails: data.clientEmails?.length ?? 0 });
    // Full envelope: per-address invite results ride beside `data`.
    const envelope = await fetchApiEnvelope<ClientReview & { shareUrl: string }>('/client-reviews', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    });
    return { ...envelope.data, invites: Array.isArray(envelope.invites) ? (envelope.invites as ClientEmailInvite[]) : [] };
  },

  /** Replace a link's client emails. New addresses are emailed now; removed ones stop. */
  setRecipients: (token: string | undefined, id: string, emails: string[]): Promise<{ clients: ClientReviewRecipient[]; invites: ClientEmailInvite[] }> => {
    log.info('[api/clientReviews] setRecipients', { id, count: emails.length });
    return fetchApi('/client-reviews/' + id + '/recipients', {
      token,
      method: 'PUT',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ emails }),
      expectedErrorStatuses: [400],
    });
  },

  createLiveReview: async (token: string | undefined, data: { projectId: string; pageUrl: string }): Promise<CreateLiveReviewResult> => {
    log.info('[api/clientReviews] createLiveReview', { projectId: data.projectId });
    // Read the full envelope: SPA hosts get auto-switched to snapshot and the
    // server reports it via modeSwitched/switchReason as siblings of `data`,
    // which the plain fetchApi unwrap would discard.
    const envelope = await fetchApiEnvelope<ClientReview & { shareUrl: string }>(
      `/projects/${data.projectId}/live-review`,
      {
        token,
        method: 'POST',
        body: JSON.stringify({ pageUrl: data.pageUrl }),
        // LiveReviewModal renders every 400 this route returns (a domain that
        // does not resolve, a private address, a figma.com design link) as an
        // inline field error, so the refusal is not a crash. Logs at warn
        // instead of minting a fake $exception (incident 2026-09-01, PostHog
        // inbox report 01a05e4b). See errorLogLevel.ts and projectShares.create.
        expectedErrorStatuses: [400],
      },
    );
    return {
      ...envelope.data,
      liveAvailable: envelope.liveAvailable === true,
      modeSwitched: envelope.modeSwitched === true,
      switchReason: typeof envelope.switchReason === 'string' ? envelope.switchReason : undefined,
      responseMode: (envelope.mode === 'streamed' || envelope.mode === 'snapshot' || envelope.mode === 'live')
        ? (envelope.mode as 'live' | 'snapshot' | 'streamed')
        : undefined,
      liveViewUrl: typeof envelope.liveViewUrl === 'string' ? envelope.liveViewUrl : undefined,
      authHandoff: typeof envelope.authHandoff === 'string' ? envelope.authHandoff : undefined,
    };
  },

  /**
   * Pre-flight probe: can the review proxy meaningfully render this URL?
   * Called by the onboarding wizard before creating a team review so a
   * login-walled / unreachable site routes to the extension path instead of
   * a broken canvas (TODOS #73). Callers treat any failure as reviewable
   * (fail open) — the server route itself already fails open too.
   */
  preflight: (token: string | undefined, url: string): Promise<{ reviewable: boolean; reason: string }> => {
    log.info('[api/clientReviews] preflight', { url });
    return fetchApi<{ reviewable: boolean; reason: string }>('/review-preflight', {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify({ url }),
    });
  },

  list: (token: string | undefined, projectId: string): Promise<Array<ClientReview & { commentCount: number }>> => {
    log.info('[api/clientReviews] list', { projectId });
    return fetchApi<Array<ClientReview & { commentCount: number }>>(`/client-reviews/project/${projectId}`, {
      token,
      timeoutMs: SCREEN_READ_TIMEOUT_MS,
    });
  },

  update: async (
    token: string | undefined,
    id: string,
    data: { revoke?: boolean; expiresAt?: string | null; allowResponsiveViews?: boolean },
  ): Promise<ClientReview> => {
    log.info('[api/clientReviews] update', { id });
    const review = await fetchApi<ClientReview>(`/client-reviews/${id}`, {
      token,
      method: 'PATCH',
      timeoutMs: MUTATION_TIMEOUT_MS,
      body: JSON.stringify(data),
    });
    if (data.allowResponsiveViews !== undefined && review.allowResponsiveViews !== data.allowResponsiveViews) {
      throw new Error('Responsive view settings are not available on the server yet. Please try again after the update.');
    }
    return review;
  },

  delete: (token: string | undefined, id: string): Promise<void> => {
    log.info('[api/clientReviews] delete', { id });
    return fetchApi<void>(`/client-reviews/${id}`, {
      token,
      method: 'DELETE',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },

  /**
   * Refresh a snapshot review's stored page IN PLACE — same token, same
   * comments, same tickets.
   *
   * A stored snapshot references the target site's build-hashed stylesheets, so
   * that site's next deploy 404s them and the snapshot renders unstyled
   * (measured on production 2026-08-28: 8 of 66 active snapshot reviews). Before
   * this, the only remedy was creating a NEW link, which mints a new URL and
   * strands the comment thread — useless for a link already sent to a client.
   *
   * Returns as soon as the capture is QUEUED, not when it finishes: the server
   * flips the review to `capturing` and works asynchronously, and the review
   * page renders its own branded progress state meanwhile.
   */
  recapture: (token: string | undefined, id: string): Promise<{ id: string; status: string }> => {
    log.info('[api/clientReviews] recapture', { id });
    return fetchApi<{ id: string; status: string }>(`/client-reviews/${id}/recapture`, {
      token,
      method: 'POST',
      timeoutMs: MUTATION_TIMEOUT_MS,
    });
  },

  /**
   * Re-mint a fresh review auth handoff (Fix 7 bounce). A signed-in team
   * member stuck on the GUEST render clicks 'Sign in or Create an account' -> the
   * /review-handoff/:token page calls this -> the server verifies membership
   * and returns the full review URL with a fresh ?s= appended. 403 with the
   * server's message when the caller isn't on the review project's team.
   *
   * Bounded: this is the exact request that stranded user-0fa99b@example.com on
   * a permanent spinner on 2026-08-12 (GitHub issue #75). See
   * HANDOFF_REQUEST_TIMEOUT_MS above for why it sits under the page's own
   * deadline.
   */
  remintReviewHandoff: (token: string | undefined, reviewToken: string, context?: URLSearchParams): Promise<{ url: string }> => {
    log.info('[api/clientReviews] remintReviewHandoff');
    return fetchApi<{ url: string }>(`/review/${encodeURIComponent(reviewToken)}/handoff${context?.toString() ? `?${context.toString()}` : ''}`, {
      token,
      method: 'POST',
      timeoutMs: HANDOFF_REQUEST_TIMEOUT_MS,
    });
  },

  /**
   * Attach an anonymous free-tool session to the caller's account (OVE-4).
   *
   * The guest annotated with no account (OVE-3 removed the wall); after they
   * sign up, the dashboard POSTs here so the ephemeral project + its tickets +
   * client_comments re-parent into `teamId` and nothing is lost. The server route
   * (packages/server/src/routes/clientReviews.ts:1211) is requireFullSession, so a
   * review-scoped token is refused — this MUST run with the Clerk-authed dashboard
   * session. Idempotent: a repeat call returns alreadyClaimed=true. The server
   * fires free_scan_claimed on the re-parent so the data-attach step is joinable
   * to account_created and the originating scan.
   *
   * Every refusal useFreeScanClaim surfaces ITSELF as a toast is opted into
   * expectedErrorStatuses, so it stays off console.error (no fake $exception):
   * 400 (not_claimable / invalid_team), 403 (not_author), 404 (bad token),
   * 409 (claim-race, claimed_by_other, not_ephemeral) and 410 (expired).
   *
   * The list is derived from the hook's `terminal` set (useFreeScanClaim.ts:89)
   * mapped through the route's status codes (handoffRouter.ts:174-198), NOT from
   * whichever statuses somebody happened to hit. 400 was the one that got away:
   * the route answers it for not_claimable (:193) and invalid_team (:195), the
   * hook lists both as terminal and toasts them, and the omission put those two
   * handled refusals on log.error -- the branch PostHog's console-error
   * autocapture files as a fake $exception (the PR #77/#79 class, same as
   * createLiveReview's URL-refusal 400 and projectShares' self-share 400).
   * 403/404 are already global-handled (errorLogLevel.ts HANDLED_HTTP_STATUSES);
   * they stay listed so this reads as the whole contract rather than a residue.
   */
  claim: (
    token: string | undefined,
    reviewToken: string,
    data: { teamId?: string } = {},
  ): Promise<ClaimFreeScanResult> => {
    log.info('[api/clientReviews] claim');
    return fetchApi<ClaimFreeScanResult>(`/review/${encodeURIComponent(reviewToken)}/claim`, {
      token,
      method: 'POST',
      body: JSON.stringify(data),
      timeoutMs: CLAIM_REQUEST_TIMEOUT_MS,
      // LOAD-BEARING, not belt-and-braces. The server authorises this claim on
      // the httpOnly `reviewerId` cookie it minted on the author's first
      // comment, so possession of the link alone cannot re-parent a stranger's
      // session. That cookie lives on the API origin, and this is a
      // cross-ORIGIN request (app.overlayqa.com -> api.overlayqa.com), so
      // fetch's default `same-origin` credentials mode DROPS it and every
      // legitimate claim would come back 403 not_author. The API answers
      // `credentials: true` on CORS for this exact origin (app.ts), and the
      // cookie is SameSite=Lax on a shared registrable domain, so it rides a
      // same-site cross-origin POST once credentials are requested.
      credentials: 'include',
      expectedErrorStatuses: [400, 403, 404, 409, 410],
    });
  },
};
